/**
 * Workspace storage layer
 * Manages both local (IndexedDB) and remote (D1) workspaces
 */

import type { Workspace, WorkspaceMetadata, CreateWorkspaceParams, UpdateWorkspaceParams, WorkspaceStats } from '../workspace-types';
import { nanoid } from 'nanoid';

const DB_NAME = 'archimate-workspaces';
const DB_VERSION = 3;
const WORKSPACE_STORE = 'workspaces';
const DIAGRAM_STORE = 'diagrams';
const FOLDER_STORE = 'folders';

// Current workspace ID (stored in localStorage)
const CURRENT_WORKSPACE_KEY = 'archimate-current-workspace';

/**
 * Open or upgrade the IndexedDB database
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Workspaces store
      if (!db.objectStoreNames.contains(WORKSPACE_STORE)) {
        const workspaceStore = db.createObjectStore(WORKSPACE_STORE, { keyPath: 'id' });
        workspaceStore.createIndex('type', 'type', { unique: false });
        workspaceStore.createIndex('isDefault', 'isDefault', { unique: false });
      }

      // Diagrams store (now workspace-scoped)
      if (!db.objectStoreNames.contains(DIAGRAM_STORE)) {
        const diagramStore = db.createObjectStore(DIAGRAM_STORE, { keyPath: 'id' });
        diagramStore.createIndex('workspaceId', 'workspaceId', { unique: false });
        diagramStore.createIndex('folderId', 'folderId', { unique: false });
        diagramStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }

      // Folders store (now workspace-scoped)
      if (!db.objectStoreNames.contains(FOLDER_STORE)) {
        const folderStore = db.createObjectStore(FOLDER_STORE, { keyPath: 'id' });
        folderStore.createIndex('workspaceId', 'workspaceId', { unique: false });
        folderStore.createIndex('parentId', 'parentId', { unique: false });
      }
    };
  });
}

/**
 * Initialize database with default workspace if needed
 */
export async function initializeWorkspaces(): Promise<void> {
  const workspaces = await getAllWorkspaces();

  if (workspaces.length === 0) {
    // Create default workspace
    await createWorkspace({
      name: 'My Architecture Workspace',
      description: 'Default workspace for architecture diagrams',
      type: 'local',
    }, true);
  }

  // Migrate diagrams without workspaceId to default workspace
  await migrateDiagramsToDefaultWorkspace();
}

/**
 * Migrate diagrams without workspaceId to the default workspace
 */
async function migrateDiagramsToDefaultWorkspace(): Promise<void> {
  const db = await openDB();
  const tx = db.transaction([DIAGRAM_STORE, WORKSPACE_STORE], 'readwrite');
  const diagramStore = tx.objectStore(DIAGRAM_STORE);
  const workspaceStore = tx.objectStore(WORKSPACE_STORE);

  return new Promise((resolve) => {
    const diagramsRequest = diagramStore.getAll();
    diagramsRequest.onsuccess = () => {
      const diagrams = diagramsRequest.result;

      // Find default workspace
      const workspacesRequest = workspaceStore.getAll();
      workspacesRequest.onsuccess = () => {
        const workspaces = workspacesRequest.result as Workspace[];
        const defaultWorkspace = workspaces.find(w => w.isDefault) || workspaces[0];

        if (!defaultWorkspace) {
          resolve();
          return;
        }

        // Update diagrams without workspaceId synchronously to avoid transaction auto-commit
        for (const diagram of diagrams) {
          if (!diagram.workspaceId) {
            diagram.workspaceId = defaultWorkspace.id;
            diagramStore.put(diagram);
          }
        }

        resolve();
      };
      workspacesRequest.onerror = () => resolve();
    };
    diagramsRequest.onerror = () => resolve();
  });
}

/**
 * Create a new workspace
 */
export async function createWorkspace(
  params: CreateWorkspaceParams,
  setAsDefault: boolean = false
): Promise<Workspace> {
  // If setting as default, unset other defaults FIRST (before opening our transaction)
  if (setAsDefault) {
    const allWorkspaces = await getAllWorkspaces();
    for (const ws of allWorkspaces) {
      if (ws.isDefault) {
        await updateWorkspace(ws.id, { isDefault: false });
      }
    }
  }

  const workspace: Workspace = {
    id: nanoid(),
    name: params.name,
    description: params.description,
    type: params.type,
    isDefault: setAsDefault,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  // Now open transaction and put - no awaits between transaction open and put
  const db = await openDB();
  const tx = db.transaction(WORKSPACE_STORE, 'readwrite');
  const store = tx.objectStore(WORKSPACE_STORE);

  return new Promise((resolve, reject) => {
    const request = store.put(workspace);
    request.onsuccess = async () => {
      // If remote workspace, sync to server (after transaction completes)
      if (params.type === 'remote') {
        try {
          await syncWorkspaceToRemote(workspace);
        } catch (e) {
          console.error('Failed to sync workspace to remote:', e);
        }
      }
      resolve(workspace);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all workspaces with metadata
 */
export async function getAllWorkspaces(): Promise<WorkspaceMetadata[]> {
  const db = await openDB();
  const tx = db.transaction([WORKSPACE_STORE, DIAGRAM_STORE, FOLDER_STORE], 'readonly');
  const workspaceStore = tx.objectStore(WORKSPACE_STORE);
  const diagramStore = tx.objectStore(DIAGRAM_STORE);
  const folderStore = tx.objectStore(FOLDER_STORE);

  return new Promise((resolve) => {
    const request = workspaceStore.getAll();
    request.onsuccess = () => {
      const workspaces = request.result as Workspace[];

      if (workspaces.length === 0) {
        resolve([]);
        return;
      }

      // Start all count requests immediately (before any awaits)
      // This keeps the transaction alive
      const countPromises = workspaces.map(workspace => {
        const diagramPromise = countByIndex(diagramStore, 'workspaceId', workspace.id);
        const folderPromise = countByIndex(folderStore, 'workspaceId', workspace.id);
        return Promise.all([diagramPromise, folderPromise]).then(([diagramCount, folderCount]) => ({
          ...workspace,
          diagramCount,
          folderCount,
        }));
      });

      // Wait for all counts to complete
      Promise.all(countPromises).then(resolve).catch(() => resolve([]));
    };
    request.onerror = () => resolve([]);
  });
}

/**
 * Get a specific workspace by ID
 */
export async function getWorkspace(id: string): Promise<Workspace | null> {
  const db = await openDB();
  const tx = db.transaction(WORKSPACE_STORE, 'readonly');
  const store = tx.objectStore(WORKSPACE_STORE);

  return new Promise((resolve) => {
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => resolve(null);
  });
}

/**
 * Update workspace properties
 */
export async function updateWorkspace(
  id: string,
  params: UpdateWorkspaceParams
): Promise<Workspace | null> {
  const workspace = await getWorkspace(id);
  if (!workspace) return null;

  // If setting as default, unset other defaults FIRST (before opening our transaction)
  if (params.isDefault) {
    const allWorkspaces = await getAllWorkspaces();
    for (const ws of allWorkspaces) {
      if (ws.id !== id && ws.isDefault) {
        // Update each workspace in its own transaction
        const db = await openDB();
        const tx = db.transaction(WORKSPACE_STORE, 'readwrite');
        const store = tx.objectStore(WORKSPACE_STORE);
        await new Promise<void>((resolve, reject) => {
          const updated = { ...ws, isDefault: false, updatedAt: Date.now() };
          const request = store.put(updated);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      }
    }
  }

  const updated: Workspace = {
    ...workspace,
    ...params,
    updatedAt: Date.now(),
  };

  // Now open transaction and put - no awaits between transaction open and put
  const db = await openDB();
  const tx = db.transaction(WORKSPACE_STORE, 'readwrite');
  const store = tx.objectStore(WORKSPACE_STORE);

  return new Promise((resolve, reject) => {
    const request = store.put(updated);
    request.onsuccess = async () => {
      // If remote workspace, sync to server (after transaction completes)
      if (workspace.type === 'remote') {
        try {
          await syncWorkspaceToRemote(updated);
        } catch (e) {
          console.error('Failed to sync workspace to remote:', e);
        }
      }
      resolve(updated);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a workspace and all its contents
 */
export async function deleteWorkspace(id: string): Promise<void> {
  const workspace = await getWorkspace(id);
  if (!workspace) return;

  const db = await openDB();
  const tx = db.transaction([WORKSPACE_STORE, DIAGRAM_STORE, FOLDER_STORE], 'readwrite');
  const diagramStore = tx.objectStore(DIAGRAM_STORE);
  const folderStore = tx.objectStore(FOLDER_STORE);
  const workspaceStore = tx.objectStore(WORKSPACE_STORE);

  await new Promise<void>((resolve, reject) => {
    // Get all diagrams and folders first
    const diagramIndex = diagramStore.index('workspaceId');
    const folderIndex = folderStore.index('workspaceId');
    const diagramRequest = diagramIndex.getAll(id);
    const folderRequest = folderIndex.getAll(id);

    let diagramsToDelete: { id: string }[] = [];
    let foldersToDelete: { id: string }[] = [];
    let gotDiagrams = false;
    let gotFolders = false;

    const tryDelete = () => {
      if (!gotDiagrams || !gotFolders) return;

      // Delete all in the same transaction (synchronously)
      for (const diagram of diagramsToDelete) {
        diagramStore.delete(diagram.id);
      }
      for (const folder of foldersToDelete) {
        folderStore.delete(folder.id);
      }

      // Delete the workspace
      const deleteRequest = workspaceStore.delete(id);
      deleteRequest.onsuccess = () => resolve();
      deleteRequest.onerror = () => reject(deleteRequest.error);
    };

    diagramRequest.onsuccess = () => {
      diagramsToDelete = diagramRequest.result;
      gotDiagrams = true;
      tryDelete();
    };
    diagramRequest.onerror = () => {
      gotDiagrams = true;
      tryDelete();
    };

    folderRequest.onsuccess = () => {
      foldersToDelete = folderRequest.result;
      gotFolders = true;
      tryDelete();
    };
    folderRequest.onerror = () => {
      gotFolders = true;
      tryDelete();
    };
  });

  // Handle post-deletion logic (after transaction completes)
  const currentId = getCurrentWorkspaceId();
  if (currentId === id) {
    const remaining = await getAllWorkspaces();
    const defaultWs = remaining.find(ws => ws.isDefault) || remaining[0];
    if (defaultWs) {
      setCurrentWorkspaceId(defaultWs.id);
    }
  }

  // If remote workspace, delete from server
  if (workspace.type === 'remote') {
    try {
      await deleteWorkspaceFromRemote(id);
    } catch (e) {
      console.error('Failed to delete workspace from remote:', e);
    }
  }
}

/**
 * Get workspace statistics
 */
export async function getWorkspaceStats(workspaceId: string): Promise<WorkspaceStats> {
  const db = await openDB();
  const tx = db.transaction([DIAGRAM_STORE, FOLDER_STORE], 'readonly');
  const diagramStore = tx.objectStore(DIAGRAM_STORE);
  const folderStore = tx.objectStore(FOLDER_STORE);

  const diagramIndex = diagramStore.index('workspaceId');
  const folderIndex = folderStore.index('workspaceId');

  return new Promise((resolve) => {
    const diagramRequest = diagramIndex.getAll(workspaceId);
    const folderRequest = folderIndex.getAll(workspaceId);

    Promise.all([
      new Promise<any[]>((res) => {
        diagramRequest.onsuccess = () => res(diagramRequest.result);
      }),
      new Promise<any[]>((res) => {
        folderRequest.onsuccess = () => res(folderRequest.result);
      }),
    ]).then(([diagrams, folders]) => {
      const stats: WorkspaceStats = {
        totalDiagrams: diagrams.length,
        totalFolders: folders.length,
        totalElements: diagrams.reduce((sum, d) => sum + (d.elementCount || 0), 0),
        totalRelationships: diagrams.reduce((sum, d) => sum + (d.relationshipCount || 0), 0),
        lastModified: Math.max(
          ...diagrams.map(d => d.updatedAt),
          ...folders.map(f => f.updatedAt),
          0
        ),
      };
      resolve(stats);
    });
  });
}

/**
 * Get or create the default workspace
 */
export async function getDefaultWorkspace(): Promise<Workspace> {
  const workspaces = await getAllWorkspaces();
  const defaultWs = workspaces.find(ws => ws.isDefault);

  if (defaultWs) {
    return defaultWs;
  }

  // Create default workspace if none exists
  return await createWorkspace({
    name: 'My Architecture Workspace',
    description: 'Default workspace for architecture diagrams',
    type: 'local',
  }, true);
}

/**
 * Get the current workspace ID from localStorage
 */
export function getCurrentWorkspaceId(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(CURRENT_WORKSPACE_KEY);
}

/**
 * Set the current workspace ID in localStorage
 */
export function setCurrentWorkspaceId(id: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(CURRENT_WORKSPACE_KEY, id);
}

/**
 * Get the current workspace (or default)
 */
export async function getCurrentWorkspace(): Promise<Workspace> {
  const currentId = getCurrentWorkspaceId();

  if (currentId) {
    const workspace = await getWorkspace(currentId);
    if (workspace) return workspace;
  }

  return await getDefaultWorkspace();
}

// Helper function to count items by index
function countByIndex(store: IDBObjectStore, indexName: string, value: string): Promise<number> {
  return new Promise((resolve) => {
    const index = store.index(indexName);
    const request = index.count(value);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(0);
  });
}

// Remote sync functions (to be implemented with API endpoints)
async function syncWorkspaceToRemote(workspace: Workspace): Promise<void> {
  try {
    await fetch('/api/secure/archimate/workspaces', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(workspace),
    });
  } catch (error) {
    console.error('Failed to sync workspace to remote:', error);
  }
}

async function deleteWorkspaceFromRemote(id: string): Promise<void> {
  try {
    await fetch(`/api/secure/archimate/workspaces/${id}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error('Failed to delete workspace from remote:', error);
  }
}
